import { createFileRoute } from "@tanstack/react-router"

import { pageHead } from "@/lib/metadata"
import {
  LegalBody,
  LegalList,
  LegalSection,
} from "@/components/layout/legal-prose"
import { PageHeader, SiteShell } from "@/components/layout/site-shell"
import { siteConfig } from "@/lib/site"

/** Written from the app's actual data flows. Update this page and the
 * retention policy together whenever those flows change. */
function PrivacyPage() {
  return (
    <SiteShell>
      <PageHeader title="How we handle your data">
        <p>
          This notice applies when you browse {siteConfig.name}, create an
          account, buy a plan, send a support ticket, or claim a gift. We
          collect what we need to run the site and answer you; we do not sell
          personal data or use it for advertising.
        </p>
      </PageHeader>

      <LegalBody updated={siteConfig.contentUpdated}>
        <LegalSection title="Who we are">
          <p>
            {siteConfig.legalName}, trading as {siteConfig.name}, operates this
            website and is responsible for the personal data described here.
            {siteConfig.businessAddress && (
              <> Our business address is {siteConfig.businessAddress}.</>
            )}{" "}
            Contact us at{" "}
            <a href={`mailto:${siteConfig.contactEmail}`}>
              {siteConfig.contactEmail}
            </a>{" "}
            for privacy requests.
          </p>
          <p>
            Your account pages, support tickets, launch-list and beta programs
            and gifts, and the downloading and deletion of your data, are
            handled by Anysola Account at account.anysola.com. It is run by the
            same company and keeps its data with the same database provider.
            This website keeps the help articles, news, changelog and status
            pages and the status and news email subscriptions.
          </p>
        </LegalSection>

        <LegalSection title="What we collect">
          <LegalList
            items={[
              {
                term: "Visitors",
                description:
                  "Aggregate page-view counts from Cloudflare Web Analytics, which sets no cookies and does not track you across sites. We do not build advertising profiles from it.",
              },
              {
                term: "Account",
                description:
                  "Email, name, username, and avatar if you provide them, plus sign-in method and last sign-in information through Clerk.",
              },
              {
                term: "Support tickets",
                description:
                  "Subject, messages and replies, optional company name, account email/name, the IP address of the ticket and of each reply sent from the website, content-rule acknowledgement time, operational status and priority, unread state, email-delivery identifiers, and any internal support note. A ticket is emailed only after its database write succeeds. Email replies are accepted only at a per-ticket authenticated recipient address, then checked against the account email.",
              },
              {
                term: "AI support assistant",
                description:
                  "When it’s switched on, an AI assistant answers support tickets first and says that it is one. To answer, it reads your ticket and, from your own account only, your plan and billing status, credit balance and recent credit history. It passes anything about payments, refunds, credits, security, legal matters or closing your account to a person, and you can ask for a person at any time. For 90 days we keep a record of each time it handled a ticket: the outcome, which lookups it used, and its cost, but not your messages again.",
              },
              {
                term: "Support administration",
                description:
                  "We keep a limited audit trail inside each ticket for status, priority, internal-note, and support-reply actions, including the administrator account and time, plus a record of when you removed a ticket from your own dashboard. The audit trail records that a note changed, not its contents.",
              },
              {
                term: "Abuse prevention",
                description:
                  "Rate-limit counters by account and IP. The longest rate-limit window is one hour.",
              },
              {
                term: "Launch updates",
                description:
                  "If you ask to hear when a new app is ready: your account identifier, verified account email, consent time, request IP, form source, and a hash of the random unsubscribe credential. We email the credential but do not store a usable copy. Joining requires a signed-in account, so nobody can add an address that isn’t theirs.",
              },
              {
                term: "Beta programs",
                description:
                  "Your account identifier, name and verified email, application answers, acknowledgement time, application status, private review notes, decision history, and personal access instructions. We also keep queued emails and delivery results. Beta announcement unsubscribe links are signed and tied to the subscription that received them.",
              },
              {
                term: "Gifts",
                description:
                  "When you claim a gift: the gift code, claim time, and the reward issued. Anonymous claims are tied to a random visitor identifier kept in a signed cookie; signed-in claims to a keyed hash of your account identifier, not the identifier itself. Anonymous claims pass a Cloudflare Turnstile check, and claim attempts are rate-limited by IP.",
              },
              {
                term: "Status notifications",
                description:
                  "If you subscribe to incident emails: your account identifier, account email, and when you subscribed or left.",
              },
              {
                term: "News emails",
                description:
                  "If you subscribe to news emails: your account identifier, account email, and when you subscribed or left, plus one record per post we email you with whether it was sent. Emails go to your account’s current verified address, and each one carries a signed unsubscribe link.",
              },
              {
                term: "Anysola Chat",
                description:
                  "If you use Anysola Chat: your conversations (titles, your messages and its replies, the web sources it cited and the code it ran) and the files you attach or it produces, stored on our own server under your account identifier. We also keep one usage record per reply (token counts, web searches and cost) to enforce usage limits. Deleting a conversation deletes its messages and files.",
              },
              {
                term: "Anysola Cloud",
                description:
                  "If you use Anysola Cloud: your API keys (their names, which services each may use, when each was made, last used and revoked, and a fingerprint of the key, never the key itself) and one record per API request: the service and endpoint, the response status, the credits charged, how long it took and when, kept for 90 days. What a request sends to a service is not stored unless that service says so.",
              },
              {
                term: "Billing",
                description:
                  "If you buy a plan: Polar handles the purchase as merchant of record and holds your payment details, billing address, tax information, and invoices. We keep your account identifier, your Polar customer identifier, which plan you are on, whether it is a trial, when the current period ends, and whether it is set to cancel. If we give you a plan without payment, we record the plan, its end date, and why. Billing actions our staff take on your account, such as cancellations, refunds and granted plans, are logged with the staff member, time, and amount. We never see or store your card details.",
              },
              {
                term: "Anysola credits",
                description:
                  "Your credit and API credit balances, and one record per movement of credits: which app spent them, how many, the short description the app gave, and when; refunds from an app; credits our staff added or removed, with who did it and why; and each API credit pack you bought (the order, the product and the credits it added). Closing your account deletes the balances, the history and the pack records.",
              },
              {
                term: "Teams",
                description:
                  "If you pay for a Team plan and invite people, or are invited to one: for a seat, your account identifier and the name and email you and the team’s owner each had when you joined, and the date you joined; for an invite, the owner’s account identifier, name and email, the invited email address (which may not belong to an account yet) and when it was sent and expires. The owner sees the names and emails of the people on their team, and each of them sees the owner’s. We email the invited address once, naming the owner and giving their email address; replies go to the owner. A seat is not stored as active or inactive: it counts for as long as the owner’s own plan is Team.",
              },
            ]}
          />
        </LegalSection>

        <LegalSection title="Why we use it">
          <p>
            Gift data lets us deliver the rewards you ask for and keep them from
            being abused. Status and news emails are sent because you
            subscribed; you can stop them at any time. Anysola Chat
            conversations are stored to show you your history and are processed
            to answer you.
          </p>
          <p>
            Beta application data lets us review applications, select testers,
            provide personal invitations, and send application updates. Applying
            does not subscribe you to the launch list. Leaving the launch list
            stops launch announcements without withdrawing your applications.
          </p>
          <p>
            We use account and support information to provide features you
            request and answer you; security and limited audit information for
            our legitimate interests in operating and protecting the service;
            and aggregate analytics to understand whether the site works.
          </p>
          <p>
            Launch emails are sent on the consent you give in the launch-list
            form. You can withdraw it at any time. The support acknowledgement
            confirms content rules and is not consent to process the ticket.
          </p>
        </LegalSection>

        <LegalSection title="Service providers">
          <LegalList
            items={[
              {
                term: "Clerk",
                description:
                  "Provides accounts, sign-in, and profile features.",
              },
              {
                term: "Frankly Mail",
                description:
                  "Hosts our anysola.com mailboxes. Sends ticket receipts, reply notifications, launch-update confirmations, beta announcements and application updates, news and incident emails, and team invitations, and receives your email replies to support tickets.",
              },
              {
                term: "MongoDB",
                description:
                  "Stores support-ticket copies, launch-update signups, beta programs and applications, gift claims, status and news subscriptions, plan status, team seats and invitations, and email queues and delivery results.",
              },
              {
                term: "Cloudflare",
                description:
                  "Runs the Turnstile bot check on anonymous gift claims and, through Clerk, on sign-in.",
              },
              {
                term: "Polar",
                description:
                  "Sells our paid plans as merchant of record: it takes payment, calculates and remits sales tax and VAT, issues invoices, and runs the billing portal. Purchases are also handled under Polar’s own terms and privacy policy.",
              },
              {
                term: "AI model providers",
                description:
                  "The AI support assistant runs on a language model from Anthropic (Claude), reached directly or through a model gateway such as OpenRouter. Your ticket and the account details the assistant looks up are sent to that provider to write each answer, and are processed on our behalf under its API terms. Anysola Chat runs on Anthropic’s API: your messages, attachments and the pages it reads are sent to Anthropic to write each reply, and its web searches run through Anthropic. Data files you attach are uploaded to Anthropic’s file storage so its code sandbox can read them, and are deleted there when you delete the conversation or your account.",
              },
              {
                term: "Google",
                description:
                  "Stores backup copies of our database in Google Drive.",
              },
              {
                term: "Vercel",
                description:
                  "Hosts the application and provides aggregate site-use measurements.",
              },
            ]}
          />
          <p>
            These providers handle information for the services described above
            under their own terms and data-processing arrangements. We do not
            sell personal data or share it for advertising.
          </p>
        </LegalSection>

        <LegalSection title="International processing">
          <p>
            Service providers may process information outside your country. The
            location and transfer mechanism depend on the provider and service.
            We use the provider agreements and legally required safeguards
            applicable to our role. Contact us for current location and
            safeguard details relevant to your request.
          </p>
        </LegalSection>

        <LegalSection title="Cookies">
          <p>
            Clerk uses cookies needed for sign-in and account features, and the
            dashboard stores your sidebar preference in a cookie. Claiming a
            gift sets a signed visitor cookie for up to a year so the same
            browser can find its reward again. We do not use advertising or
            cross-site marketing cookies. If we add optional cookies that
            require consent, we will ask before using them.
          </p>
        </LegalSection>

        <LegalSection title="Retention and deletion">
          <p>
            Clerk retains account data under its account settings and policies.
            A successfully recorded support ticket, including its messages and
            embedded audit trail, is automatically removed from our support
            database two years after it was created. Rate-limit windows last no
            longer than one hour.
          </p>
          <p>
            Deleting your account deletes the support tickets recorded under it,
            removes it from the launch list, ends any status or news
            subscription, deletes your Anysola Chat conversations and files
            (usage records are kept for our cost accounting without your account
            identifier), deletes your Anysola Cloud API keys and request
            records, and immediately cancels any paid plan, asking Polar to
            delete and anonymise your customer record. Polar may keep anonymised
            order and tax records it is legally required to retain. Clerk
            notifies us when an account is removed, and that notification erases
            those tickets, their messages, and their audit trail from our
            support database. You can delete your account and download a copy of
            your data from the Security page of your Anysola Account. To request
            deletion of something specific without closing your account, email{" "}
            <a href={`mailto:${siteConfig.contactEmail}`}>
              {siteConfig.contactEmail}
            </a>{" "}
            and we will respond within the time required by applicable law.
          </p>
          <p>
            A team seat is kept until you leave the team, its owner removes you,
            or either of you deletes your account. Deleting an owner’s account
            also removes every seat on their team and every invite they sent. An
            invite is removed when it is accepted, declined or cancelled, and
            otherwise expires after fourteen days; one sent to your address is
            not linked to your account, so deleting the account does not remove
            it earlier.
          </p>
          <p>
            A launch-list entry is kept while you are subscribed. Leaving the
            list stops the emails immediately and keeps a record that you left
            for thirty days, so you cannot be re-added by mistake; after that
            the record is deleted too.
          </p>
          <p>
            A status or news subscription is kept while you are subscribed;
            after you leave, the record is deleted after thirty days. Records of
            news emails sent to you are deleted after 180 days. Your credit
            history (each spend, refund and adjustment) is deleted ninety days
            after the entry was made; your balance is unaffected. A record of
            each API credit pack you bought is kept while your account exists,
            so a pack is never credited twice. Gift claims are kept for as long
            as the gift exists; email us to have yours removed sooner.
          </p>
          <p>
            Database backups are kept in Google Drive and replaced on a rolling
            basis, so deleted information can remain in a backup until that
            backup is rotated out.
          </p>
          <p>
            Beta applications and review history remain available until you
            delete your account or request deletion. Withdrawing an application
            stops its review but retains its history. Beta email queue records,
            delivery results, and bounce or complaint suppression records expire
            after 180 days. Deleting your account also deletes your beta
            applications and these email records. Product access is managed by
            the product itself; contact support to revoke an existing
            invitation.
          </p>
          <p>
            The Remove button in your dashboard hides a ticket from your
            dashboard and stops you replying to it there. It does not erase our
            support record of the conversation, and it does not recall an email
            already delivered to our support inbox. Support keeps its copy until
            the two-year retention period ends, or sooner if you delete your
            account or ask us to delete it. We may retain limited information
            where the law permits or requires it, such as for an abuse report or
            legal claim.
          </p>
          <p>
            When a ticket contains abuse or other conduct we may report to the
            authorities, we can place it under legal hold and export it as an
            evidence record. The export contains the conversation, the IP
            addresses above, and the account details our identity provider holds
            (including recent sign-in IP addresses and approximate location). A
            held ticket is not deleted when the account is deleted, and evidence
            records are kept until the matter they were created for is closed.
            We share them only with authorities, courts, or our legal advisers.
          </p>
        </LegalSection>

        <LegalSection title="Your rights">
          <p>
            Your rights depend on where you live and applicable law. They may
            include asking for access, correction, deletion, restriction, or an
            objection to certain processing, and withdrawing consent where we
            rely on it. We will verify and handle requests as applicable law
            requires.
          </p>
          <p>
            You can edit your profile from the account menu. Tickets that were
            successfully saved to our database appear in your dashboard, and the
            Remove button hides one from your dashboard. Email us if you want
            our support copy deleted as well. The Security page of your Anysola
            Account downloads a copy of your data as one JSON file, including
            your Anysola Chat conversations and your Anysola Cloud keys and
            request records but not the contents of attached files, and deletes
            your account.
          </p>
          <p>
            Every launch, status and news email carries an unsubscribe link, and
            inbox providers that support one-click unsubscribe can use it
            directly. You can also unsubscribe from your dashboard at any time.
          </p>
          <p>
            Email{" "}
            <a href={`mailto:${siteConfig.contactEmail}`}>
              {siteConfig.contactEmail}
            </a>{" "}
            to make a request. You may also complain to the data-protection or
            consumer authority that applies where you live.
          </p>
        </LegalSection>

        <LegalSection title="Children">
          <p>
            The site and its account features are not intended for anyone under
            18. We do not knowingly collect personal data from children.
          </p>
        </LegalSection>

        <LegalSection title="Changes">
          <p>
            If this page changes, the date at the top changes with it. We may
            also provide a notice in the service when a change materially
            affects how we handle personal data.
          </p>
        </LegalSection>
      </LegalBody>
    </SiteShell>
  )
}

export const Route = createFileRoute("/privacy")({
  head: () =>
    pageHead({
      title: "Privacy",
      description:
        "How Anysola handles your personal data, and the rights you have.",
      path: "/privacy",
      image: "/privacy/opengraph-image",
    }),
  component: PrivacyPage,
})
